import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useBoolean } from 'usehooks-ts';

import Button from 'components/App/Button';
import ModalEditAffiliatePolicy, {
  AFFILIATE_FIELD,
} from 'components/Modals/ModalEditAffiliatePolicy';
import ModalEditCoinRefund from 'components/Modals/ModalEditCoinRefund';
import { CUSTOMER_ACCOUNT_TYPE } from 'constants/customerAccountType';
import INITIAL_VALUE from 'constants/initialValue';

import styles from './ConversionPolicy.module.scss';

const DEFAULT_COIN_REFUND_RATES = {
  [CUSTOMER_ACCOUNT_TYPE.MEMBER]: 0,
  [CUSTOMER_ACCOUNT_TYPE.TOURIST]: 0,
  [CUSTOMER_ACCOUNT_TYPE.AGENT]: 0,
  [CUSTOMER_ACCOUNT_TYPE.AMBASSADOR]: 0,
};

const DEFAULT_AFFILIATE_POLICY = {
  [AFFILIATE_FIELD.INVITE_USER]: 0,
  [AFFILIATE_FIELD.INVITED_USER]: 0,
  [AFFILIATE_FIELD.INVITE_USER_SERVICE_PERCENT]: 0,
};

const mapBookingBonusPercent = (bookingBonusPercent = {}) => ({
  [CUSTOMER_ACCOUNT_TYPE.MEMBER]: bookingBonusPercent.member ?? 0,
  [CUSTOMER_ACCOUNT_TYPE.TOURIST]: bookingBonusPercent.tourist ?? 0,
  [CUSTOMER_ACCOUNT_TYPE.AGENT]: bookingBonusPercent.agent ?? 0,
  [CUSTOMER_ACCOUNT_TYPE.AMBASSADOR]: bookingBonusPercent.ambassador ?? 0,
});

const mapAffiliateBonus = (affiliateBonus = {}) => ({
  [AFFILIATE_FIELD.INVITE_USER]: affiliateBonus.inviteUser ?? 0,
  [AFFILIATE_FIELD.INVITED_USER]: affiliateBonus.invitedUser ?? 0,
  [AFFILIATE_FIELD.INVITE_USER_SERVICE_PERCENT]: affiliateBonus.inviteUserServicePercent ?? 0,
});

function ConversionPolicy(props) {
  const {
    settingCoin,
    isLoadingUpdateSettingCoin,
    actionGetSettingCoin,
    actionUpdateSettingCoin,
  } = props;

  const [coinRefundRates, setCoinRefundRates] = useState(DEFAULT_COIN_REFUND_RATES);
  const [affiliatePolicy, setAffiliatePolicy] = useState(DEFAULT_AFFILIATE_POLICY);

  const {
    value: isOpenEditCoinRefundModal,
    setTrue: onOpenEditCoinRefundModal,
    setFalse: onCloseEditCoinRefundModal,
  } = useBoolean(false);

  const {
    value: isOpenEditAffiliateModal,
    setTrue: onOpenEditAffiliateModal,
    setFalse: onCloseEditAffiliateModal,
  } = useBoolean(false);

  useEffect(() => {
    actionGetSettingCoin();
  }, [actionGetSettingCoin]);

  useEffect(() => {
    if (settingCoin?.bookingBonusPercent) {
      setCoinRefundRates(mapBookingBonusPercent(settingCoin.bookingBonusPercent));
    }

    if (settingCoin?.affiliateBonus) {
      setAffiliatePolicy(mapAffiliateBonus(settingCoin.affiliateBonus));
    }
  }, [settingCoin]);

  const onSubmitCoinRefund = useCallback((values) => {
    actionUpdateSettingCoin({
      data: {
        bookingBonusPercent: values,
      },
      callback: () => {
        setCoinRefundRates(mapBookingBonusPercent(values));
        onCloseEditCoinRefundModal();
      },
    });
  }, [actionUpdateSettingCoin, onCloseEditCoinRefundModal]);

  const onSubmitAffiliate = useCallback((values) => {
    actionUpdateSettingCoin({
      data: {
        affiliateBonus: values,
      },
      callback: () => {
        setAffiliatePolicy(mapAffiliateBonus(values));
        onCloseEditAffiliateModal();
      },
    });
  }, [actionUpdateSettingCoin, onCloseEditAffiliateModal]);

  return (
    <div className={styles.PolicyRow}>
      <div className={styles.PolicyCard}>
        <div className={styles.PolicyCardHeader}>
          <h3 className={styles.PolicyCardTitle}>
            Hoàn xu theo loại tài khoản
          </h3>

          <Button
            type="button"
            outline
            classes={styles.EditButton}
            onClick={onOpenEditCoinRefundModal}
          >
            Chỉnh sửa
          </Button>
        </div>

        <div className={styles.PolicyList}>
          <div className={styles.PolicyRowItem}>
            <span className={styles.PolicyLabel}>Tài khoản Thành viên</span>
            <span className={styles.PolicyValue}>
              {`Hoàn xu ${coinRefundRates[CUSTOMER_ACCOUNT_TYPE.MEMBER]}%`}
            </span>
          </div>
          <div className={styles.PolicyRowItem}>
            <span className={styles.PolicyLabel}>Tài khoản Du khách</span>
            <span className={styles.PolicyValue}>
              {`Hoàn xu ${coinRefundRates[CUSTOMER_ACCOUNT_TYPE.TOURIST]}%`}
            </span>
          </div>
          <div className={styles.PolicyRowItem}>
            <span className={styles.PolicyLabel}>Tài khoản Đại lý</span>
            <span className={styles.PolicyValue}>
              {`Hoàn xu ${coinRefundRates[CUSTOMER_ACCOUNT_TYPE.AGENT]}%`}
            </span>
          </div>
          <div className={styles.PolicyRowItem}>
            <span className={styles.PolicyLabel}>Tài khoản Đại sứ</span>
            <span className={styles.PolicyValue}>
              {`Hoàn xu ${coinRefundRates[CUSTOMER_ACCOUNT_TYPE.AMBASSADOR]}%`}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.PolicyCard}>
        <div className={styles.PolicyCardHeader}>
          <h3 className={styles.PolicyCardTitle}>
            Chính sách tiếp thị liên kết
          </h3>

          <Button
            type="button"
            outline
            classes={styles.EditButton}
            onClick={onOpenEditAffiliateModal}
          >
            Chỉnh sửa
          </Button>
        </div>

        <div className={styles.PolicyList}>
          <div className={styles.PolicyRowItem}>
            <span className={styles.PolicyLabel}>Người mời</span>
            <span className={styles.PolicyValue}>
              {`${affiliatePolicy[AFFILIATE_FIELD.INVITE_USER]} xu`}
            </span>
          </div>
          <div className={styles.PolicyRowItem}>
            <span className={styles.PolicyLabel}>Người được mời</span>
            <span className={styles.PolicyValue}>
              {`${affiliatePolicy[AFFILIATE_FIELD.INVITED_USER]} xu`}
            </span>
          </div>
          <div className={styles.PolicyRowItem}>
            <span className={styles.PolicyLabel}>Người mời thưởng thêm</span>
            <span className={styles.PolicyValue}>
              {`${affiliatePolicy[AFFILIATE_FIELD.INVITE_USER_SERVICE_PERCENT]}%`}
            </span>
          </div>
        </div>

        <div className={styles.PolicyNotes}>
          <p className={styles.PolicyNote}>
            Người mời nhận được xu khi người mới hoàn thành dịch vụ đầu tiên.
          </p>
          <p className={styles.PolicyNote}>
            {`Người mời thưởng thêm ${affiliatePolicy[AFFILIATE_FIELD.INVITE_USER_SERVICE_PERCENT]}% trên mỗi dịch vụ hoàn thành từ người được mời`}
          </p>
        </div>
      </div>

      <ModalEditCoinRefund
        isOpen={isOpenEditCoinRefundModal}
        initialValues={coinRefundRates}
        isLoading={isLoadingUpdateSettingCoin}
        onToggleModal={onCloseEditCoinRefundModal}
        onSubmit={onSubmitCoinRefund}
      />

      <ModalEditAffiliatePolicy
        isOpen={isOpenEditAffiliateModal}
        initialValues={affiliatePolicy}
        isLoading={isLoadingUpdateSettingCoin}
        onToggleModal={onCloseEditAffiliateModal}
        onSubmit={onSubmitAffiliate}
      />
    </div>
  );
}

ConversionPolicy.propTypes = {
  settingCoin: PropTypes.instanceOf(Object),
  isLoadingUpdateSettingCoin: PropTypes.bool,
  actionGetSettingCoin: PropTypes.func,
  actionUpdateSettingCoin: PropTypes.func,
};

ConversionPolicy.defaultProps = {
  settingCoin: {},
  isLoadingUpdateSettingCoin: false,
  actionGetSettingCoin: INITIAL_VALUE.FUNCTION,
  actionUpdateSettingCoin: INITIAL_VALUE.FUNCTION,
};

export default ConversionPolicy;
